import { useEffect, useMemo } from "react";
import { usePagination, useTable } from "react-table";
import { Spinner } from "reactstrap";
import { find, map } from "lodash";

import DropdownActions from "../dropdown/dropdown-actions";
import Pagination from "./pagination";
import NoData from "./no-data";
import ColumnSettings from "./column-settings";

// Columnas ocultas por usuario y por tabla (preferencia de este navegador). Si no se
// puede leer o escribir (modo privado, almacenamiento bloqueado), se muestran todas.
const COLUMNS_STORAGE_PREFIX = 'growme-table-columns:';

const readHiddenColumns = (key) => {
  if (!key) return [];
  try {
    const value = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_PREFIX + key) || '[]');
    return Array.isArray(value) ? value : [];
  } catch (error) {
    return [];
  }
};

const writeHiddenColumns = (key, hiddenColumns) => {
  if (!key) return;
  try {
    localStorage.setItem(COLUMNS_STORAGE_PREFIX + key, JSON.stringify(hiddenColumns));
  } catch (error) {
    // Sin almacenamiento la preferencia dura solo mientras la página esté abierta.
  }
};

const TableComponent = ({
  classNameBody,
  pagination,
  checkbox,
  columns,
  loading,
  data,
  actions,
  options,
  section,
  itemChecked,
  allItemsChecked,
  handleSetLimit,
  handleCheckOne,
  handleAllChecked,
  handleChangePage,
  handleOpenDetails,
  handleOptionAction,
  handleAcceptNoData,
  controlledPageCount,
  manualPagination,
  emptyListInformer = 'large',
  header,
  // Cantidad inicial de filas visibles cuando no se usa paginación (por
  // defecto react-table pagina en bloques de 10). Opcional y retrocompatible:
  // si no se define, se mantiene el comportamiento anterior.
  initialPageSize,
  // Opcional: identificador de la tabla (incluye el usuario) para que cada usuario
  // elija qué columnas ver (botón + al final de la cabecera). También deja la tabla
  // con desplazamiento horizontal. Sin él, la tabla se comporta como antes.
  columnSettingsKey
}) => {

  const initialHiddenColumns = useMemo(() => readHiddenColumns(columnSettingsKey), [columnSettingsKey]);

  const tableOptions = {
    manualPagination,
    autoResetPage: false,
    // Mantener las columnas ocultas aunque cambien los datos (paginación, búsqueda).
    autoResetHiddenColumns: false,
    // autoResetPagination: true,
    columns,
    data,
    pageCount: controlledPageCount,
    initialState: {
      ...(initialPageSize ? { pageSize: initialPageSize } : {}),
      hiddenColumns: initialHiddenColumns,
    },
  };

  const {
    // basic table
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    // pagination    
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    canPreviousPage,
    canNextPage,
    // columnas visibles / ocultas
    allColumns,
    toggleHideColumn,
    setHiddenColumns,
    state: { pageIndex, pageSize, hiddenColumns }
  } = useTable(tableOptions, usePagination);

  // Columnas que el usuario puede ocultar: todas menos la de acciones.
  const configurableColumns = allColumns.filter((column) => column.type !== 'actions');
  // La primera columna (p. ej. el nombre) queda siempre visible.
  const lockedColumnId = configurableColumns[0]?.id;

  useEffect(() => {
    writeHiddenColumns(columnSettingsKey, hiddenColumns);
  }, [columnSettingsKey, hiddenColumns]);

  // `initialState.pageSize` solo se aplica al montar la tabla; si los datos
  // llegan después (ej. luego de un fetch) o cambian de tamaño, hay que
  // sincronizar el pageSize manualmente para seguir mostrando todas las filas
  // en tablas sin paginación.
  useEffect(() => {
    if (initialPageSize) {
      setPageSize(initialPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPageSize]);

  return (
    <div className="qs-component-table-container">
      <div>
        {loading &&
          <div className="qs-table-container gm-table-loading">
            <Spinner />
          </div>
        }
        {!loading && data.length > 0 &&
          <div className={`qs-table-container ${!checkbox && 'qs-no-border'} ${columnSettingsKey ? 'gm-table-scroll' : ''}`}>
            <table {...getTableProps()} cellSpacing={0}>
              <thead>
                {// Loop over the header rows
                  headerGroups.map((headerGroup, index) => (
                    // Apply the header row props
                    <tr {...headerGroup.getHeaderGroupProps()} key={`${index}-head-row`} >
                      {checkbox && <th className="gm-table-checkbox-container">
                        <div className='gm-table-checkbox'>
                          <input
                            type="checkbox"
                            id="cb-all"
                            onChange={handleAllChecked}
                            checked={allItemsChecked}
                          />
                        </div>
                      </th>}
                      {// Loop over the headers in each row
                        headerGroup.headers.map((column, index) => (
                          // Apply the header cell props
                          <th  key={`${index}-column`} {...column.getHeaderProps()} className={header === "primary-green" ? "qs-table-header" : "qs-table-header-light"}>
                            {// Render the header
                              column.render('Header')}
                          </th>
                        ))}
                      {columnSettingsKey && (
                        <th className={`gm-table-columns-th ${header === "primary-green" ? "qs-table-header" : "qs-table-header-light"}`}>
                          <ColumnSettings
                            columns={configurableColumns}
                            lockedColumnId={lockedColumnId}
                            onToggle={(id) => toggleHideColumn(id)}
                            onShowAll={() => setHiddenColumns([])}
                            onHideAll={() => setHiddenColumns(configurableColumns.filter((column) => column.id !== lockedColumnId).map((column) => column.id))}
                          />
                        </th>
                      )}
                    </tr>
                  ))}
              </thead>
              {/* Apply the table body props */}
              <tbody {...getTableBodyProps()}>
                {// Loop over the table rows
                  page.map((row, rowInx) => {
                    // Prepare the row for display
                    prepareRow(row);
                    const checked = find(itemChecked, e => row.original.id === e.id)
                    return (
                      // Apply the row props
                      <tr {...row.getRowProps()} key={`${rowInx}-page-row`}>
                        {checkbox && <td>
                          <div className='gm-table-body-checkbox'>
                            <input
                              type="checkbox"
                              id="cb-data"
                              checked={checked?.checked || false}
                              value={checked?.value}
                              onChange={(e) => handleCheckOne(e.target.checked, row.original)}
                            />
                          </div>
                        </td>}
                        {// Loop over the rows cells
                          row.cells.map((cell, index) => {
                            // Apply the cell props
                            return (
                              <td
                                key={`${index}-cell`}
                                {...cell.getCellProps()}
                                className={classNameBody ? classNameBody: "qs-table-body"}
                                onClick={(e) => {
                                  if (handleOpenDetails && cell.column?.type !== 'actions') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleOpenDetails(row.original, rowInx);
                                  }
                                }}
                              >
                                {cell.column?.type === 'actions' ?
                                  <div className="gm-table-action-container">
                                    {(map(actions, (e, index) => (
                                      e?.dropdown ?
                                        <div className='gm-actions-dropdown' key={`${index}-dropdown-actions`}>
                                          <DropdownActions
                                            options={options}
                                            handleOnClick={(action) => handleOptionAction(action, row.original)}
                                            Icon={e.icon}
                                            iconSize={16}
                                          />
                                        </div>
                                        :
                                        <button className={`gm-action-btn ${e?.className || ''}`} type='button' key={`action-${index}`} onClick={() => e.action(row.original)}>
                                          <e.icon size={16} />
                                        </button>
                                    )))}
                                  </div>
                                  :
                                  cell.render('Cell')
                                }
                              </td>
                            )
                          })}
                        {columnSettingsKey && <td className={classNameBody ? classNameBody : "qs-table-body"} />}
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        }
        {pagination && data.length > 0 &&
          <Pagination
            pageOptions={pageOptions}
            handleSetLimit={handleSetLimit}
            handleChangePage={handleChangePage}
            pageCount={pageCount}
            gotoPage={gotoPage}
            nextPage={nextPage}
            previousPage={previousPage}
            setPageSize={setPageSize}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageIndex={pageIndex}
            pageSize={pageSize}
            manualPagination={manualPagination}
          />
        }
      </div>

      {data.length === 0 && !loading &&
        <div className={`gm-nodata-main-container ${emptyListInformer === 'small' ? 'gm-small-container' : 'gm-large-container'}`}>
          <NoData section={section || ''} handleAcceptNoData={handleAcceptNoData} />
        </div>
      }
    </div>
  )
};

export default TableComponent;